import React, { useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Divider, Grid, LinearProgress,
  MenuItem, Paper, Select, Stack, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, TextField, Typography,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import SyncIcon from "@mui/icons-material/Sync";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { TabCard } from "./menuTabsUi";
import { MenuTabProps } from "./MenuTabs";
import {
  NAVY, BORDER, TEXT_SECONDARY, TEXT_PRIMARY, SUCCESS, ERROR, WARNING,
} from "../../../theme/tokens";
import { useNotifier } from "../../../Core/Notifier";
import {
  useGetPosConnectionsQuery, useGetPosConnectionQuery, useStartPosOauthMutation,
  useDisconnectPosMutation, useUpdatePosSettingsMutation, useImportPosMenuMutation,
  useSyncPosNowMutation, useReconcilePosMutation, useGetPosSyncRunsQuery,
  useGetPosItemsQuery,
} from "../../../Api/attoDeskApi";
import MenuMappingPanel from "./Pos/MenuMappingPanel";

/** Purple = integration accent; teal = the established POS accent. */
const POS_TEAL = "#00838f";
const RETURN_KEY = "pos_oauth_return";

const fmtPrice = (cents: number, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency }).format((cents || 0) / 100);

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleString() : "—";

/* ------------------------------------------------------------------ */
/*  Small presentational helpers                                        */
/* ------------------------------------------------------------------ */

const SummaryCard: React.FC<{ label: string; value: React.ReactNode; hint?: string; accent?: string }> =
  ({ label, value, hint, accent = POS_TEAL }) => (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, p: 2, height: "100%" }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: TEXT_SECONDARY }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 26, color: accent, lineHeight: 1.2, mt: 0.5 }}>
        {value}
      </Typography>
      {hint && <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>{hint}</Typography>}
    </Paper>
  );

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.6, gap: 2 }}>
    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600, textAlign: "right" }}>{children}</Typography>
  </Box>
);

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    connected: { label: "Connected", color: SUCCESS, bg: "rgba(22,163,74,0.12)" },
    auth_expired: { label: "Auth expired", color: ERROR, bg: "rgba(239,68,68,0.12)" },
    revoked: { label: "Disconnected", color: TEXT_SECONDARY, bg: "#EEF2F6" },
    error: { label: "Error", color: ERROR, bg: "rgba(239,68,68,0.12)" },
  };
  const s = map[status] || map.error;
  return <Chip size="small" label={s.label} sx={{ fontWeight: 700, color: s.color, bgcolor: s.bg }} />;
};

/* ------------------------------------------------------------------ */
/*  Main component                                                      */
/* ------------------------------------------------------------------ */

const PosSyncTab: React.FC<Partial<MenuTabProps>> = ({ companyId, playlistId }) => {
  const { showMessage, showErrorMessage } = useNotifier();

  // Connections for this company (multi-location = several; we surface the
  // first here and note that multi-location selection lands with mapping).
  const { data: listResp, isLoading: listLoading } =
    useGetPosConnectionsQuery(companyId ? { company_id: companyId } : undefined);
  const connections: any[] = (listResp?.data as any[]) ?? [];
  const activeConn = connections.find((c) => c.status !== "revoked") ?? connections[0] ?? null;
  const connectionId: number | null = activeConn?.id ?? null;

  // Rich status payload (A–E state, summary, current run). Poll while syncing.
  const { data: statusResp, refetch: refetchStatus, isFetching: statusFetching } =
    useGetPosConnectionQuery(connectionId as number, {
      skip: !connectionId,
      pollingInterval: 0,
    });
  const payload = statusResp?.data as any;
  const state: string = payload?.state ?? (connectionId ? "connected" : "not_connected");

  // Poll only in the syncing state.
  useGetPosConnectionQuery(connectionId as number, {
    skip: !connectionId || state !== "syncing",
    pollingInterval: 4000,
  });

  const [startOauth, { isLoading: starting }] = useStartPosOauthMutation();
  const [disconnect, { isLoading: disconnecting }] = useDisconnectPosMutation();
  const [importMenu, { isLoading: importing }] = useImportPosMenuMutation();
  const [syncNow, { isLoading: syncingNow }] = useSyncPosNowMutation();
  const [reconcile, { isLoading: reconciling }] = useReconcilePosMutation();

  const beginConnect = async () => {
    try {
      sessionStorage.setItem(
        RETURN_KEY,
        playlistId ? `/playlist/edit/${playlistId}` : window.location.pathname,
      );
    } catch { /* non-fatal */ }
    try {
      const res: any = await startOauth({ company_id: companyId || undefined }).unwrap();
      const url = res?.data?.authorize_url;
      if (url) window.location.href = url;
      else showErrorMessage("Could not start the Clover connection.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Clover is not configured on the server yet.");
    }
  };

  const doImport = async () => {
    if (!connectionId) return;
    try { await importMenu(connectionId).unwrap(); showMessage("Import started"); refetchStatus(); }
    catch (e: any) { showErrorMessage(e?.data?.message || "Could not start the import."); }
  };
  const doSync = async () => {
    if (!connectionId) return;
    try { await syncNow(connectionId).unwrap(); showMessage("Sync started"); refetchStatus(); }
    catch (e: any) { showErrorMessage(e?.data?.message || "Could not start the sync."); }
  };
  const doReconcile = async () => {
    if (!connectionId) return;
    try { await reconcile(connectionId).unwrap(); showMessage("Reconciliation started"); refetchStatus(); }
    catch (e: any) { showErrorMessage(e?.data?.message || "Could not start reconciliation."); }
  };
  const doDisconnect = async () => {
    if (!connectionId) return;
    try { await disconnect(connectionId).unwrap(); showMessage("Clover disconnected"); }
    catch (e: any) { showErrorMessage(e?.data?.message || "Could not disconnect."); }
  };

  const headerRight = (
    <Chip size="small" icon={<StorageIcon sx={{ fontSize: 16 }} />} label="Clover"
      sx={{ fontWeight: 700, color: POS_TEAL, bgcolor: "rgba(0,131,143,0.10)" }} />
  );

  /* ----------------------------- states ----------------------------- */

  if (listLoading) {
    return (
      <TabCard title="POS Sync" subtitle="Clover menu sync control centre" icon={<StorageIcon fontSize="small" />} right={headerRight}>
        <Box sx={{ textAlign: "center", py: 5 }}><CircularProgress sx={{ color: POS_TEAL }} /></Box>
      </TabCard>
    );
  }

  // State A — Not connected
  if (!connectionId || state === "not_connected") {
    return (
      <TabCard title="POS Sync" subtitle="Clover menu sync control centre" icon={<StorageIcon fontSize="small" />} right={headerRight}>
        <Box sx={{ textAlign: "center", py: 4, maxWidth: 560, mx: "auto" }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: "50%", mx: "auto", mb: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(0,131,143,0.10)", color: POS_TEAL,
          }}>
            <StorageIcon />
          </Box>
          <Typography sx={{ fontWeight: 800, color: NAVY, fontSize: 18, mb: 0.5 }}>Connect Clover</Typography>
          <Typography sx={{ fontSize: 13.5, color: TEXT_SECONDARY, mb: 2.5 }}>
            Import categories, items, prices, images and availability from your Clover
            merchant, map them onto your screens, and keep them in sync. Clover stays
            the source of your menu data — you control exactly how it looks on the TV.
          </Typography>
          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 1.75, mb: 2.5, textAlign: "left" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY, mb: 0.5 }}>Requested permissions (read-only)</Typography>
            <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY }}>
              Inventory (read) · Merchant (read) · Orders (read). VM Display never writes back to Clover.
            </Typography>
          </Paper>
          <Button onClick={beginConnect} disabled={starting} variant="contained"
            startIcon={starting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <StorageIcon />}
            sx={{ textTransform: "none", fontWeight: 800, bgcolor: POS_TEAL, "&:hover": { bgcolor: "#006974" }, px: 3 }}>
            Connect Clover
          </Button>
        </Box>
      </TabCard>
    );
  }

  const conn = payload?.connection ?? activeConn ?? {};
  const summary = payload?.summary ?? {};
  const run = payload?.current_run;

  const ConnectionFacts = (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2 }}>
      <InfoRow label="Merchant">{conn.merchant_name || "—"}</InfoRow>
      <InfoRow label="Merchant ID">{conn.merchant_id_masked}</InfoRow>
      <InfoRow label="Environment">{conn.environment}</InfoRow>
      <InfoRow label="Status"><StatusChip status={conn.status} /></InfoRow>
      <InfoRow label="Last sync">{fmtDate(conn.last_sync_at)}</InfoRow>
    </Paper>
  );

  // State E — Error / auth expired
  if (state === "error") {
    return (
      <TabCard title="POS Sync" subtitle="Clover menu sync control centre" icon={<StorageIcon fontSize="small" />} right={headerRight}>
        <Paper elevation={0} sx={{ border: `1px solid ${ERROR}`, bgcolor: "rgba(239,68,68,0.06)", borderRadius: 2, p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
            <ErrorOutlineIcon sx={{ color: ERROR }} />
            <Box>
              <Typography sx={{ fontWeight: 800, color: NAVY }}>
                {conn.status === "auth_expired" ? "Clover authorization expired" : "Clover sync error"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
                {run?.error || "The last operation failed. Reconnect Clover or retry."}
              </Typography>
            </Box>
          </Box>
        </Paper>
        {ConnectionFacts}
        <Stack direction="row" spacing={1.25} sx={{ mt: 2 }}>
          <Button onClick={beginConnect} variant="contained"
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: POS_TEAL, "&:hover": { bgcolor: "#006974" } }}>
            Reconnect Clover
          </Button>
          <Button onClick={() => refetchStatus()} startIcon={<RefreshIcon />} variant="outlined"
            sx={{ textTransform: "none", fontWeight: 700 }}>Retry</Button>
          <Button onClick={doDisconnect} disabled={disconnecting} startIcon={<LinkOffIcon />} color="inherit"
            sx={{ textTransform: "none", fontWeight: 700, color: TEXT_SECONDARY }}>Disconnect</Button>
        </Stack>
      </TabCard>
    );
  }

  // State D — Syncing
  if (state === "syncing") {
    const counts = run?.counts || {};
    return (
      <TabCard title="POS Sync" subtitle="Clover menu sync control centre" icon={<StorageIcon fontSize="small" />} right={headerRight}>
        <Box sx={{ py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <SyncIcon sx={{ color: POS_TEAL, animation: "spin 1.2s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }} />
            <Typography sx={{ fontWeight: 800, color: NAVY }}>Syncing from Clover…</Typography>
          </Box>
          <LinearProgress sx={{ borderRadius: 1, height: 8, "& .MuiLinearProgress-bar": { bgcolor: POS_TEAL } }} />
          <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY, mt: 1 }}>
            {counts.categories != null
              ? `Imported ${counts.items ?? 0} items across ${counts.categories ?? 0} categories…`
              : "Fetching catalog from Clover…"}
          </Typography>
        </Box>
        {ConnectionFacts}
      </TabCard>
    );
  }

  // State B — Connected, not imported
  if (state === "connected") {
    return (
      <TabCard title="POS Sync" subtitle="Clover menu sync control centre" icon={<StorageIcon fontSize="small" />} right={headerRight}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>{ConnectionFacts}</Grid>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, height: "100%" }}>
              <Typography sx={{ fontWeight: 800, color: NAVY, mb: 0.5 }}>Ready to import</Typography>
              <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 2 }}>
                Pull your Clover categories, items, prices and images into a draft you can
                map to screens. Nothing publishes to your TVs until you choose to.
              </Typography>
              <Stack direction="row" spacing={1.25}>
                <Button onClick={doImport} disabled={importing} variant="contained"
                  startIcon={importing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CloudDownloadIcon />}
                  sx={{ textTransform: "none", fontWeight: 800, bgcolor: POS_TEAL, "&:hover": { bgcolor: "#006974" } }}>
                  Import menu
                </Button>
                <Button onClick={doDisconnect} disabled={disconnecting} startIcon={<LinkOffIcon />} color="inherit"
                  sx={{ textTransform: "none", fontWeight: 700, color: TEXT_SECONDARY }}>Disconnect</Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </TabCard>
    );
  }

  // State C — Imported (control centre)
  return (
    <ImportedControlCentre
      connectionId={connectionId}
      playlistId={playlistId}
      conn={conn}
      summary={summary}
      headerRight={headerRight}
      connectionFacts={ConnectionFacts}
      onSync={doSync} onReconcile={doReconcile} onDisconnect={doDisconnect}
      syncing={syncingNow} reconciling={reconciling} disconnecting={disconnecting}
      refetchStatus={refetchStatus} statusFetching={statusFetching}
    />
  );
};

/* ------------------------------------------------------------------ */
/*  Imported state — summary cards + sub-tabs                           */
/* ------------------------------------------------------------------ */

const ImportedControlCentre: React.FC<{
  connectionId: number;
  playlistId?: number;
  conn: any;
  summary: any;
  headerRight: React.ReactNode;
  connectionFacts: React.ReactNode;
  onSync: () => void; onReconcile: () => void; onDisconnect: () => void;
  syncing: boolean; reconciling: boolean; disconnecting: boolean;
  refetchStatus: () => void; statusFetching: boolean;
}> = ({
  connectionId, playlistId, conn, summary, headerRight, connectionFacts,
  onSync, onReconcile, onDisconnect, syncing, reconciling, disconnecting, refetchStatus, statusFetching,
}) => {
  const [sub, setSub] = useState(0);

  return (
    <TabCard title="POS Sync" subtitle="Clover menu sync control centre" icon={<StorageIcon fontSize="small" />}
      right={
        <Stack direction="row" spacing={1}>
          <Button onClick={onSync} disabled={syncing} size="small" startIcon={<SyncIcon />} variant="contained"
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: POS_TEAL, "&:hover": { bgcolor: "#006974" } }}>
            Sync now
          </Button>
          {headerRight}
        </Stack>
      }>
      {/* Four summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><SummaryCard label="Categories" value={summary.categories ?? 0} /></Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Menu items" value={summary.items ?? 0}
            hint={`${summary.items_with_images ?? 0} with images`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Pending changes" value={summary.pending_changes ?? 0}
            accent={(summary.pending_changes ?? 0) > 0 ? WARNING : POS_TEAL} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Unavailable" value={summary.unavailable_items ?? 0}
            accent={(summary.unavailable_items ?? 0) > 0 ? ERROR : POS_TEAL} />
        </Grid>
      </Grid>

      <Tabs value={sub} onChange={(_, v) => setSub(v)} variant="scrollable" scrollButtons="auto"
        sx={{ borderBottom: `1px solid ${BORDER}`, mb: 2, minHeight: 40, "& .MuiTab-root": { textTransform: "none", fontWeight: 700, minHeight: 40 }, "& .Mui-selected": { color: POS_TEAL }, "& .MuiTabs-indicator": { backgroundColor: POS_TEAL } }}>
        <Tab label="Overview" />
        <Tab label="Menu Mapping" />
        <Tab label="Price Changes" />
        <Tab label="Availability" />
        <Tab label="Sync History" />
        <Tab label="Settings" />
      </Tabs>

      {sub === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>{connectionFacts}</Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, height: "100%" }}>
              <Typography sx={{ fontWeight: 800, color: NAVY, mb: 1 }}>Catalog health</Typography>
              <InfoRow label="Items with images">{summary.items_with_images ?? 0}</InfoRow>
              <InfoRow label="Items without images">{summary.items_without_images ?? 0}</InfoRow>
              <InfoRow label="Modifier groups">{summary.modifiers ?? 0}</InfoRow>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={1.25}>
                <Button onClick={onReconcile} disabled={reconciling} size="small" startIcon={<RefreshIcon />} variant="outlined"
                  sx={{ textTransform: "none", fontWeight: 700 }}>Full reconcile</Button>
                <Button onClick={() => refetchStatus()} size="small" disabled={statusFetching} color="inherit"
                  sx={{ textTransform: "none", fontWeight: 700, color: TEXT_SECONDARY }}>Refresh</Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {sub === 1 && <MenuMappingPanel connectionId={connectionId} playlistId={playlistId} />}
      {sub === 2 && <PriceChangesComingSoon pending={summary.pending_changes ?? 0} />}
      {sub === 3 && <AvailabilityPanel connectionId={connectionId} />}
      {sub === 4 && <SyncHistoryPanel connectionId={connectionId} />}
      {sub === 5 && (
        <SettingsPanel connectionId={connectionId} conn={conn}
          onDisconnect={onDisconnect} disconnecting={disconnecting} />
      )}
    </TabCard>
  );
};

/* ----------------------------- sub-panels ----------------------------- */

const PriceChangesComingSoon: React.FC<{ pending: number }> = ({ pending }) => (
  <Box sx={{ textAlign: "center", py: 4, maxWidth: 520, mx: "auto" }}>
    <Typography sx={{ fontWeight: 800, color: NAVY, mb: 0.5 }}>Price Changes</Typography>
    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
      When Clover prices change, they arrive here as pending changes so an admin can
      approve, schedule or reject each one before it reaches your public boards —
      the safe default for restaurants. {pending} change{pending === 1 ? "" : "s"} pending.
    </Typography>
    <Chip label="Wired in the change-workflow phase" size="small" sx={{ mt: 1.5, fontWeight: 700, bgcolor: "#EEF2F6", color: TEXT_SECONDARY }} />
  </Box>
);

const AvailabilityPanel: React.FC<{ connectionId: number }> = ({ connectionId }) => {
  const { data, isFetching } = useGetPosItemsQuery({ id: connectionId, unavailable_only: true });
  const items: any[] = (data?.data as any[]) ?? [];
  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 1.5 }}>
        Items Clover currently reports as unavailable. Sold-out changes apply to your
        screens immediately (the recommended default); layout position is preserved so
        an item returns automatically when Clover marks it available again.
      </Typography>
      {isFetching ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={22} sx={{ color: POS_TEAL }} /></Box>
      ) : items.length === 0 ? (
        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>All imported items are currently available.</Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden" }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Status</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>{it.name}</TableCell>
                  <TableCell>{fmtPrice(it.price_cents, it.currency)}</TableCell>
                  <TableCell align="right"><Chip size="small" label="Sold out" sx={{ fontWeight: 700, color: ERROR, bgcolor: "rgba(239,68,68,0.12)" }} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

const SyncHistoryPanel: React.FC<{ connectionId: number }> = ({ connectionId }) => {
  const { data, isFetching } = useGetPosSyncRunsQuery(connectionId);
  const runs: any[] = (data?.data as any[]) ?? [];
  const statusColor = (s: string) => s === "success" ? SUCCESS : s === "failed" ? ERROR : s === "partial" ? WARNING : TEXT_SECONDARY;
  if (isFetching) return <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={22} sx={{ color: POS_TEAL }} /></Box>;
  if (runs.length === 0) return (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 3, textAlign: "center" }}>
      <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>No syncs yet.</Typography>
    </Paper>
  );
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden" }}>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Trigger</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Result</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Started</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {runs.map((r) => (
            <TableRow key={r.id}>
              <TableCell sx={{ textTransform: "capitalize" }}>{String(r.type).replace(/_/g, " ")}</TableCell>
              <TableCell sx={{ textTransform: "capitalize" }}>{r.trigger}</TableCell>
              <TableCell>
                <Chip size="small" label={r.status} sx={{ fontWeight: 700, textTransform: "capitalize", color: statusColor(r.status), bgcolor: `${statusColor(r.status)}22` }} />
                {r.counts?.items != null && <Typography component="span" sx={{ fontSize: 12, color: TEXT_SECONDARY, ml: 1 }}>{r.counts.items} items</Typography>}
              </TableCell>
              <TableCell>{fmtDate(r.started_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

const SettingsPanel: React.FC<{
  connectionId: number; conn: any; onDisconnect: () => void; disconnecting: boolean;
}> = ({ connectionId, conn, onDisconnect, disconnecting }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [update, { isLoading }] = useUpdatePosSettingsMutation();
  const [changeMode, setChangeMode] = useState(conn.change_mode || "require_approval");
  const [availMode, setAvailMode] = useState(conn.availability_mode || "immediate");
  const [soldOutLabel, setSoldOutLabel] = useState(conn?.settings?.sold_out_label || "Sold Out");

  const dirty = useMemo(
    () => changeMode !== conn.change_mode || availMode !== conn.availability_mode
      || soldOutLabel !== (conn?.settings?.sold_out_label || "Sold Out"),
    [changeMode, availMode, soldOutLabel, conn],
  );

  const save = async () => {
    try {
      await update({ id: connectionId, body: {
        change_mode: changeMode, availability_mode: availMode,
        settings: { ...(conn.settings || {}), sold_out_label: soldOutLabel },
      } }).unwrap();
      showMessage("Settings updated");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Could not save settings."); }
  };

  return (
    <Box sx={{ maxWidth: 620 }}>
      <Typography sx={{ fontWeight: 800, color: NAVY, mb: 0.5 }}>Price synchronization</Typography>
      <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY, mb: 1 }}>
        How Clover price changes reach your public boards.
      </Typography>
      <Select fullWidth size="small" value={changeMode} onChange={(e) => setChangeMode(e.target.value)} sx={{ mb: 2 }}>
        <MenuItem value="auto_publish">Automatic — update every screen immediately</MenuItem>
        <MenuItem value="require_approval">Approval required — hold until an admin approves (recommended)</MenuItem>
        <MenuItem value="scheduled">Scheduled — apply at a chosen time</MenuItem>
        <MenuItem value="manual_only">Manual only — show the difference, never auto-update</MenuItem>
      </Select>

      <Typography sx={{ fontWeight: 800, color: NAVY, mb: 0.5 }}>Availability</Typography>
      <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY, mb: 1 }}>
        How sold-out / back-in-stock changes behave.
      </Typography>
      <Select fullWidth size="small" value={availMode} onChange={(e) => setAvailMode(e.target.value)} sx={{ mb: 2 }}>
        <MenuItem value="immediate">Mark sold out immediately (recommended)</MenuItem>
        <MenuItem value="require_approval">Require approval</MenuItem>
      </Select>

      <TextField fullWidth size="small" label="Sold-out label" value={soldOutLabel}
        onChange={(e) => setSoldOutLabel(e.target.value)} sx={{ mb: 2 }} inputProps={{ maxLength: 40 }} />

      <Stack direction="row" spacing={1.25}>
        <Button onClick={save} disabled={!dirty || isLoading} variant="contained"
          sx={{ textTransform: "none", fontWeight: 800, bgcolor: POS_TEAL, "&:hover": { bgcolor: "#006974" } }}>
          Save settings
        </Button>
        <Button onClick={onDisconnect} disabled={disconnecting} startIcon={<LinkOffIcon />} color="inherit"
          sx={{ textTransform: "none", fontWeight: 700, color: ERROR }}>Disconnect Clover</Button>
      </Stack>
    </Box>
  );
};

export default PosSyncTab;
